import { skipToken } from '@reduxjs/toolkit/query'
import { useState, type ReactNode } from 'react'
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import ListSubheader from '@mui/material/ListSubheader'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import BoltIcon from '@mui/icons-material/Bolt'
import SearchIcon from '@mui/icons-material/Search'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import {
  metaTypeName,
  patternTypeName,
  useGetManifestQuery,
  useListComponentsQuery,
  useListTenantsQuery,
} from '../api/authoring'
import { currentClaims, isOperator, logout } from '../auth/session'
import { config } from '../config'
import { acceptsUpload, proseOf } from '../model/manifest'
import { listedTypes } from '../model/records'
import { operator, routes, siteOf, TYPE_FILTER } from '../model/routes'
import { PublicationStatus } from '../screens/site/PublicationStatus'
import { SITE_TYPE } from '../model/website'
import { Appearance } from '../ui/Appearance'
import { shortcutHint } from '../ui/CommandPalette'

/** How wide the sidebar is, in one place, because the shell reserves it. */
export const SIDEBAR_WIDTH = 268

/**
 * The navigation: everything that says *where you are and where else you
 * could be*, in a column beside the work.
 *
 * **It lists objects and tasks, not types.** What it listed before was
 * `manifest.types` — fifteen entries, one per registered component type,
 * under a Content/Build toggle that swapped the whole column for another
 * one. Both of those were the data model presented as an information
 * architecture: a reader who wanted the photo they uploaded had to know
 * that a photo is a Media, that Media is filed between Links and Menus,
 * and that all of it is in the other mode. The registry is a fact about
 * the platform; it is not a filing cabinet, and it was growing an entry
 * per type registered.
 *
 * So: four named groups. **Site** is the site as a thing — its
 * overview, its pages, its releases, its settings. **Content** is what
 * its owner puts in it, one row per record type the manifest declares
 * plus the media library. **Agency** is what the agency designs and the
 * customer mostly does not touch, and **Platform** is the machinery
 * around both. Every group carries a heading, because a run with no
 * name over it reads as the default and everything under a heading as
 * the exception. Every one of the fifteen types is still
 * reachable, through the filter on one component library, which is built
 * from `manifest.types` exactly as this list was. Nothing about "a new
 * type needs no client change" is given up; what is given up is the
 * claim that a reader should navigate by type.
 *
 * **Nothing here decides anything.** The site switcher and the tenant
 * switcher are navigation: they change which route the client is on and
 * set no value the rest of the app reads. That is why this file can be a
 * list of links and a little highlighting — every question about what a
 * path *means* is answered in `model/routes`, and asked here.
 */
export function SideNav({
  onNavigate,
  onSearch,
}: {
  onNavigate?: () => void
  /** Open the command palette — the shell's, so the button and the
      keystroke open the same one. Absent where nothing mounts it. */
  onSearch?: () => void
}) {
  const { tenant } = useParams()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const openSite = tenant ? siteOf(pathname) : undefined

  const { data: memberships } = useListTenantsQuery()
  // Skipped until a tenant is on the route, for the reason the shell
  // always skipped it: the manifest is per tenant, so there is no answer
  // to ask for before one is chosen.
  const manifest = useGetManifestQuery(tenant ? { tenant } : skipToken)
  // The same read the sites screen makes, under the same cache key, so
  // listing them here costs no request the app was not making anyway.
  const sites = useListComponentsQuery(tenant ? { tenant, type: SITE_TYPE } : skipToken)

  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const claims = currentClaims()

  const tenants = memberships?.tenants ?? []
  const listed = sites.data?.components ?? []

  // **Which site this column is about.** The open one, and otherwise the
  // first the tenant has — because the agency screens are tenant-scoped
  // and a reader standing on the pattern library is still working on a
  // site. Falling back keeps Pages and Overview one click away from
  // there rather than two, and nothing is *marked* as current that the
  // address does not say is current: selection below is computed from
  // the path alone.
  const site = openSite ?? listed[0]?.id
  const siteLabel = listed.find((candidate) => candidate.id === site)?.label ?? site

  const metaType = metaTypeName(manifest.data)
  const patternType = patternTypeName(manifest.data)
  // What an owner calls their pictures, asked of the registry: the type
  // that holds uploaded files, in its own words. A build that registers
  // none simply has no Media entry, and one that registers a second gets
  // it under the type's own name rather than under "Media" written here.
  const mediaType = (manifest.data?.types ?? []).find((declared) => acceptsUpload(manifest.data, declared.name))

  const go = (to: string) => {
    navigate(to)
    onNavigate?.()
  }

  const operating = isOperator()
  const filter = new URLSearchParams(search).get(TYPE_FILTER) ?? ''
  const componentsPath = routes.components(tenant ?? '')
  const onComponents = pathname === componentsPath
  const onAccount = pathname === routes.account()
  const under = (base: string) => pathname === base || pathname.startsWith(`${base}/`)

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography
          component={RouterLink}
          to="/"
          onClick={() => onNavigate?.()}
          variant="h2"
          sx={{ textDecoration: 'none', color: 'text.primary', display: 'block' }}
        >
          Bluu CMS
        </Typography>
      </Box>

      {tenant && (
        <>
          {/* The site switcher, at the top, because the site is what
              everything below it is about. It carries the tenant too:
              an agency holds several tenants and one site each, so two
              separate switchers would be two controls answering the same
              question — which customer am I working on. */}
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Button
              fullWidth
              color="inherit"
              endIcon={<UnfoldMoreIcon />}
              onClick={(event) => setAnchor(event.currentTarget)}
              sx={{
                textTransform: 'none',
                justifyContent: 'space-between',
                px: 1.25,
                py: 0.75,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Stack sx={{ minWidth: 0, textAlign: 'left' }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {siteLabel ?? 'No site yet'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {tenant}
                </Typography>
              </Stack>
            </Button>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
              {listed.map((candidate) => (
                <MenuItem
                  key={candidate.id}
                  selected={candidate.id === openSite}
                  onClick={() => {
                    setAnchor(null)
                    go(routes.site(tenant, candidate.id))
                  }}
                >
                  {candidate.label ?? candidate.id}
                </MenuItem>
              ))}
              <MenuItem
                onClick={() => {
                  setAnchor(null)
                  go(routes.tenant(tenant))
                }}
              >
                All sites…
              </MenuItem>
              {tenants.length > 1 && <Divider />}
              {tenants.length > 1 && (
                <ListSubheader disableSticky sx={{ bgcolor: 'transparent', lineHeight: 2.2 }}>
                  Tenants
                </ListSubheader>
              )}
              {tenants.length > 1 &&
                tenants.map((membership) => (
                  <MenuItem
                    key={membership.tenant}
                    selected={membership.tenant === tenant}
                    onClick={() => {
                      setAnchor(null)
                      // The tenant's root, not the same screen under
                      // another tenant: a site id belongs to one tenant
                      // and carrying it across would address a component
                      // that does not exist there.
                      go(routes.tenant(membership.tenant))
                    }}
                  >
                    {membership.tenant}
                  </MenuItem>
                ))}
            </Menu>
          </Box>

          {/* **A button that looks like a field, and is not one.** It
              was a disabled placeholder while nothing was behind it; the
              palette is behind it now. It is still not an input: typing
              here and typing in the palette would be two text boxes for
              one search, and the second would have to inherit the first
              one's keystrokes to avoid eating them. One click, one
              keystroke, one place the words go.

              The shortcut is drawn because a reader who finds this once
              should not have to come back to the sidebar for it. */}
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Button
              fullWidth
              color="inherit"
              onClick={() => onSearch?.()}
              startIcon={<SearchIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                px: 1.25,
                py: 0.75,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                color: 'text.secondary',
                fontWeight: 400,
              }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1, textAlign: 'left' }}>
                Search…
              </Typography>
              <Box
                component="kbd"
                sx={{
                  fontSize: 11,
                  fontFamily: 'inherit',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 0.5,
                  px: 0.5,
                  color: 'text.disabled',
                }}
              >
                {shortcutHint()}
              </Box>
            </Button>
          </Box>

          <Divider />
        </>
      )}

      {!tenant && operating && (
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', py: 1 }}>
          <OperatorNav pathname={pathname} onNavigate={onNavigate} />
        </Box>
      )}

      {tenant && (
        <>
          <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', py: 1 }}>
            {/* **The site, and then what is in it.** Four headed
                groups where there were two headed and one not: a run of
                rows with no name over it reads as the default case and
                everything below it as an exception, which is what made
                "why is Content the only thing with a heading" the first
                question anyone asked of this column. Overview, Pages,
                Releases and Settings are the site itself — the thing as
                a whole, its pages, its history and how it is configured
                — and Content is what an owner puts in it. */}
            {site && (
              <NavGroup label="Site">
                <NavItem
                  to={routes.site(tenant, site)}
                  label="Overview"
                  selected={pathname === routes.site(tenant, site)}
                  onNavigate={onNavigate}
                />
                <NavItem
                  to={routes.pages(tenant, site)}
                  label="Pages"
                  selected={under(routes.pages(tenant, site))}
                  onNavigate={onNavigate}
                />
                {/* For a small business this is one of the two reasons
                    to open the admin at all, which is the other half of
                    the placement argument: it sits with the site,
                    because it is a fact about the site rather than
                    something its owner authored. */}
                <NavItem
                  to={routes.enquiries(tenant, site)}
                  label="Enquiries"
                  selected={under(routes.enquiries(tenant, site))}
                  onNavigate={onNavigate}
                />
                <NavItem
                  to={routes.siteReleases(tenant, site)}
                  label="Releases"
                  selected={pathname === routes.siteReleases(tenant, site)}
                  onNavigate={onNavigate}
                />
                {/* The site's own settings — its theme, its search
                    metadata, its hostnames. Its routing and its
                    composition are the other two faces of that screen,
                    reached from its own tabs: four rows here for four
                    faces of one document would be a tree where the
                    screen already has a tab strip.

                    It sits with the site and no longer under Agency.
                    Everything on it is a property of *this site* that
                    its owner can be shown and asked about; what made it
                    agency work was who happens to fill it in, and that
                    is a permission, not a place. */}
                <NavItem
                  to={routes.siteSettings(tenant, site)}
                  label="Settings"
                  selected={
                    pathname === routes.siteSettings(tenant, site) ||
                    pathname === routes.siteRouting(tenant, site) ||
                    pathname === routes.siteComposition(tenant, site)
                  }
                  onNavigate={onNavigate}
                />
              </NavGroup>
            )}

            {/* …and no group at all where there is nothing in it: a
                heading over an empty run names something the reader
                then goes looking for. */}
            {site && (listedTypes(manifest.data).length > 0 || mediaType) && (
              <NavGroup label="Content">
                {listedTypes(manifest.data).map((declared) => {
                  const to = routes.records(tenant, site, declared.name)
                  return (
                    <NavItem
                      key={declared.name}
                      to={to}
                      label={proseOf(declared, declared.name).plural}
                      selected={under(to)}
                      onNavigate={onNavigate}
                    />
                  )
                })}
                {mediaType && (
                  // The media library is the tenant's, not the site's —
                  // one uploaded photo is used by every site the tenant
                  // has. It is listed here anyway because this is where
                  // an owner looks for it, and the address it opens says
                  // plainly which library it is.
                  <NavItem
                    to={routes.components(tenant, mediaType.name)}
                    label={proseOf(mediaType).plural}
                    selected={onComponents && filter === mediaType.name}
                    onNavigate={onNavigate}
                  />
                )}
              </NavGroup>
            )}

            {/* What the agency owns, and the customer mostly does not
                touch. Grouped rather than hidden: hiding it would be a
                permission the surface does not enforce, and a group with
                a heading says the same thing honestly.

                It holds design and only design now, so the "Design"
                caption that used to head all three of its rows is gone
                — a caption that covers every row of a named group is
                the group's name written twice. */}
            <NavGroup label="Agency">
              {metaType && (
                <NavItem
                  to={routes.types(tenant)}
                  label="Content types"
                  selected={under(routes.types(tenant))}
                  onNavigate={onNavigate}
                />
              )}
              {patternType && (
                <NavItem
                  to={routes.patterns(tenant)}
                  label="Patterns"
                  selected={pathname === routes.patterns(tenant)}
                  onNavigate={onNavigate}
                />
              )}
              {/* One entry where there were fifteen. Selected wherever
                  the reader is in the library or in a component opened
                  from it, except when the filter is the media one — that
                  row is above and two lit rows would be the navigation
                  disagreeing with itself. */}
              <NavItem
                to={componentsPath}
                label="Components"
                selected={
                  (onComponents && (!mediaType || filter !== mediaType.name)) ||
                  pathname.startsWith(`${componentsPath}/`)
                }
                onNavigate={onNavigate}
              />
            </NavGroup>

            {/* Everything that is about the machinery rather than about
                the site: what this tenant is wired to, and — for an
                operator — the account behind it. Integrations belongs
                here and not under Agency because a webhook is not a
                design decision; it is a connection to something outside
                the CMS, which is exactly what the rest of this group
                is. The group is no longer operator-only for that
                reason. */}
            <NavGroup label="Platform">
              <NavItem
                to={routes.integrations(tenant)}
                label="Integrations"
                selected={pathname === routes.integrations(tenant)}
                onNavigate={onNavigate}
              />
              {operating && (
                <NavItem to={operator.tenant(tenant)} label="This tenant’s account" selected={false} onNavigate={onNavigate} />
              )}
              {operating && (
                <NavItem to={operator.tenants()} label="Operator" selected={false} onNavigate={onNavigate} />
              )}
            </NavGroup>
          </Box>

          <Divider />

          <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>
            <BoltIcon fontSize="small" sx={{ color: 'text.disabled', mt: '1px' }} />
            <Box>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: 'text.primary' }}>
                Changes are private
              </Typography>
              {/* Where it stands, rather than a standing instruction: the
                  same chip the site's own header draws, so a change
                  waiting to go live is visible from every screen and one
                  click from Releases. */}
              {site ? (
                <Box sx={{ mt: 0.5 }}>
                  <PublicationStatus tenant={tenant} site={site} />
                </Box>
              ) : (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Publish them from the site&rsquo;s Releases view.
                </Typography>
              )}
            </Box>
          </Stack>
        </>
      )}

      <Divider />

      {/* The guide, beside the account: it is no group's, and somebody
          who has lost their way is the reader least able to go looking
          for it. An entry like the rest rather than a "?" in a corner,
          because a customer clicks a word they can read.

          **It opens under the site this column is about** — the open
          one, or the tenant's first from a design screen or the
          tenant's landing — so the Site and Content groups stay drawn
          beside the chapter that tells the reader to use them. Where no
          site is known (the account and the operator area outside a
          tenant, or a tenant with no site yet) there is nothing to read
          it beside, and the entry is left out rather than sent
          somewhere that would draw the menu the guide describes without
          its groups. Selected anywhere in the guide of the site it
          opens. */}
      {tenant && site && (
        <List dense disablePadding sx={{ pt: 1 }}>
          <NavItem
            to={routes.help(tenant, site)}
            label="Help"
            selected={under(routes.help(tenant, site))}
            onNavigate={onNavigate}
          />
        </List>
      )}

      {/* Who is reading, and how they want to be read to. Both are
          properties of this reader rather than of the site, which is
          why they sit together at the foot of the column and not in
          the site's Settings. The identity is the way to the account,
          wherever the reader is: an account is not a tenant's, so it
          is not in any of the groups above. */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {config.authMode === 'none' ? (
            // The link still draws: the screen behind it says the loop
            // has no account, which is the truth and is worth one click.
            <Stack spacing={0.5} alignItems="flex-start">
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                label="credential-free loop"
                title="AUTHORING_CLAIMS_REQUIRED is unset, so the surface holds every tenant and this client sends no token."
              />
              <Identity onNavigate={onNavigate} selected={onAccount} />
            </Stack>
          ) : (
            // Stacked, not a row. This pair in a row is what pushed 84px of
            // overflow off the side of every screen on a phone.
            <Stack spacing={0.5} alignItems="flex-start">
              <Identity
                onNavigate={onNavigate}
                selected={onAccount}
                name={claims?.name}
                email={claims?.email ?? claims?.sub}
              />
              <Button size="small" onClick={logout} sx={{ textTransform: 'none', px: 0.5 }}>
                Sign out
              </Button>
            </Stack>
          )}
        </Box>
        <Appearance />
      </Stack>
    </Stack>
  )
}

/**
 * Who is signed in, as the way to their account.
 *
 * Labelled *Account* in so many words, because an email at the foot of a
 * column reads as a caption and nobody clicks a caption. The name sits
 * over the email once the reader has given one (it rides on the ID
 * token through the `profile` scope, so it appears at the next refresh).
 */
function Identity({
  name,
  email,
  selected,
  onNavigate,
}: {
  name?: string
  email?: string
  selected: boolean
  onNavigate?: () => void
}) {
  const line = { maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const
  return (
    <Box
      component={RouterLink}
      to={routes.account()}
      onClick={() => onNavigate?.()}
      aria-current={selected ? 'page' : undefined}
      sx={{
        display: 'block',
        maxWidth: '100%',
        px: 0.5,
        borderRadius: 1,
        textDecoration: 'none',
        color: 'inherit',
        '&:hover, &:focus-visible': { bgcolor: 'action.hover' },
      }}
    >
      <Typography
        component="span"
        variant="caption"
        sx={{ display: 'block', fontWeight: 600, color: selected ? 'primary.main' : 'text.primary' }}
      >
        Account
      </Typography>
      {name && (
        <Typography component="span" variant="body2" sx={{ ...line, display: 'block' }}>
          {name}
        </Typography>
      )}
      {email && (
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ...line, display: 'block' }}>
          {email}
        </Typography>
      )}
    </Box>
  )
}

/**
 * The operator area's navigation. Drawn only for an identity in the
 * `operator` group, and only as a courtesy: the operator surface refuses
 * anyone else itself.
 */
function OperatorNav({ pathname, onNavigate }: { pathname: string; onNavigate?: () => void }) {
  const tenantsSelected = pathname === operator.tenants() || pathname.startsWith(`${operator.tenants()}/tenants/`)
  return (
    <NavGroup label="Operator">
      <NavItem to={operator.tenants()} label="Tenants" selected={tenantsSelected} onNavigate={onNavigate} />
      <NavItem to={operator.billing()} label="Billing" selected={pathname === operator.billing()} onNavigate={onNavigate} />
      <NavItem to={operator.plans()} label="Plans" selected={pathname === operator.plans()} onNavigate={onNavigate} />
      <NavItem
        to={operator.connections()}
        label="Traffic connections"
        selected={pathname === operator.connections()}
        onNavigate={onNavigate}
      />
    </NavGroup>
  )
}

/** A run of navigation entries, headed where the run has a name. */
function NavGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <List
      dense
      disablePadding
      sx={{ pb: 1 }}
      subheader={
        label ? (
          <ListSubheader
            disableSticky
            sx={{
              bgcolor: 'transparent',
              lineHeight: 2,
              fontSize: 11,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'text.disabled',
            }}
          >
            {label}
          </ListSubheader>
        ) : undefined
      }
    >
      {children}
    </List>
  )
}

/**
 * One entry.
 *
 * Selection is drawn with a rail and a tint rather than the default grey
 * fill, because in a column of entries "which one am I on" is the only
 * thing the eye is looking for and a hover and a selection that differ
 * by a few percent of grey do not answer it.
 */
function NavItem({
  to,
  label,
  selected,
  onNavigate,
}: {
  to: string
  label: string
  selected: boolean
  onNavigate?: () => void
}) {
  return (
    <ListItemButton
      component={RouterLink}
      to={to}
      selected={selected}
      // The rail and the tint say which screen is open to a reader who
      // can see them; this says it to everyone else.
      aria-current={selected ? 'page' : undefined}
      onClick={() => onNavigate?.()}
      sx={{
        mx: 1,
        pl: 1.5,
        borderRadius: 1,
        borderLeft: 3,
        borderColor: selected ? 'primary.main' : 'transparent',
        '&.Mui-selected': { bgcolor: 'action.selected' },
      }}
    >
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          noWrap: true,
          fontSize: 14,
          fontWeight: selected ? 600 : 400,
          color: selected ? 'primary.main' : 'text.primary',
        }}
      />
    </ListItemButton>
  )
}
